<template>
  <div class="upload-file">
    <el-upload ref="fileUpload" multiple :action="uploadFileUrl" :before-upload="handleBeforeUpload"
      :file-list="fileList" :limit="limit" :name="name" :headers="headers" :data="params" :show-file-list="false"
      class="upload-file-uploader" @error="handleUploadError" @exceed="handleExceed" @success="handleUploadSuccess">
      <el-button size="small" type="primary">选取文件</el-button>

      <template #tip v-if="showTip">
        请上传
        <template v-if="fileSize">
          大小不超过 <b style="color:#f56c6c">{{ fileSize }}MB</b>
        </template>
        <template v-if="fileType">
          格式为 <b style="color:#f56c6c">{{ fileType.join('/') }}</b>
        </template>
        的文件
        <template v-if="fileName"> <b @click="handleDownload" style="color: #f56c6c">点击下载模板</b> </template>
      </template>
    </el-upload>

    <!-- 文件列表 -->
    <transition-group tag="ul" name="el-fade-in-linear" class="upload-file-list el-upload-list el-upload-list--text">
      <li v-for="(file, index) in fileList" :key="file.uid || file.url"
        class="el-upload-list__item ele-upload-list__item-content">
        <el-link :href="`${file.url}`" target="_blank" :underline="false">
          <span class="el-icon-document">
            {{ getFileName(file.name) }}
          </span>
        </el-link>

        <div class="ele-upload-list__item-content-action">
          <el-link type="danger" :underline="false" @click="handleDelete(index)">
            删除
          </el-link>
        </div>
      </li>
    </transition-group>
  </div>
</template>

<script setup>
import { ref, watch, computed } from "vue";
import { SYS_BASE_URL, PICTURE_URL } from "@/constant/Const";
import { ElMessage } from "element-plus";

/* props */
const props = defineProps({
  modelValue: [String, Array, Object],
  limit: { type: Number, default: 5 },
  fileSize: { type: Number, default: 10 },
  name: { type: String, default: "file" },
  fileType: { type: Array, default: () => ["zip", "rar", "pdf", "jpg", "jpeg", "png", 'json', 'mp4', 'mp3'] },
  isShowTip: { type: Boolean, default: true },
  params: { type: Object, default: () => ({}) },
  uploadFileUrl: { type: String, default: "" },
  fileName: { type: String, default: "" },
});

/* emits */
const emit = defineEmits(["update:modelValue", 'success']);

/* state */
const fileUpload = ref();
const fileList = ref([]);
const uploadList = ref([]);
const number = ref(0);
const baseUrl = ref(PICTURE_URL);

const uploadFileUrl = props.uploadFileUrl;

const headers = {
  Authorization: sessionStorage.getItem("token"),
};

/* computed */
const showTip = computed(() => props.isShowTip && (props.fileType || props.fileSize));

/* watch v-model */
watch(
  () => props.modelValue,
  (val) => {
    if (!val) {
      fileList.value = [];
      return;
    }

    let uid = 1;
    const list = Array.isArray(val) ? val : String(val).split(",");
    fileList.value = list.map((item) => {
      if (typeof item === "string") {
        item = { name: item, url: item };
      }
      item.uid = item.uid || Date.now() + uid++;
      return item;
    });
  },
  { immediate: true, deep: true }
);

/* methods */


// 下载模板
const handleDownload = () => {
  window.open(SYS_BASE_URL + '/api/down/temp/'+ props.fileName +'.xlsx', "_blank");
}

const handleBeforeUpload = (file) => {
  if (props.fileType?.length) {
    const ext = file.name.split(".").pop();
    if (!props.fileType.includes(ext)) {
      ElMessage.error(`文件格式不正确，请上传 ${props.fileType.join("/")} 文件`);
      return false;
    }
  }

  if (props.fileSize) {
    const isLt = file.size / 1024 / 1024 < props.fileSize;
    if (!isLt) {
      ElMessage.error(`文件大小不能超过 ${props.fileSize} MB`);
      return false;
    }
  }

  number.value++;
  return true;
};

const handleExceed = () => {
  ElMessage.error(`上传文件数量不能超过 ${props.limit} 个`);
};

const handleUploadError = () => {
  ElMessage.error("上传文件失败，请重试");
};

const handleUploadSuccess = (res, file) => {
  if (res.code === 1 || res.code === 0) {
    const fileInfo = res.data;
    uploadList.value.push({
      name: `https://${fileInfo.domain}/${fileInfo.pathPrefix}${fileInfo.storageFilePath}${fileInfo.newFileName}`,
      url: `https://${fileInfo.domain}/${fileInfo.pathPrefix}${fileInfo.storageFilePath}${fileInfo.newFileName}`,
    });
    uploadedSuccessfully();
  } else {
    number.value--;
    ElMessage.error(res.msg);
    fileUpload.value?.handleRemove(file);
    uploadedSuccessfully();
  }
};

const handleDelete = (index) => {
  fileList.value.splice(index, 1);
  emitValue();
};

const uploadedSuccessfully = () => {
  if (number.value > 0 && uploadList.value.length === number.value) {
    fileList.value = fileList.value.concat(uploadList.value);
    uploadList.value = [];
    number.value = 0;
    emitValue();
  }
};

const emitValue = () => {
  emit("success");
  emit("update:modelValue", listToString(fileList.value));
};

const listToString = (list, separator = ",") =>
  list.map((i) => i.url).join(separator);

const getFileName = (name) =>
  name.includes("/") ? name.slice(name.lastIndexOf("/") + 1) : name;
</script>

<style scoped lang="scss">
.upload-file-uploader {
  margin-bottom: 5px;
}

.upload-file-list .el-upload-list__item {
  border: 1px solid #e4e7ed;
  line-height: 2;
  margin-bottom: 10px;
}

.ele-upload-list__item-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.ele-upload-list__item-content-action .el-link {
  margin-right: 10px;
}
</style>
